import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useGetUserLoggin, useExchangeOAuthToken } from '../hooks/useAuthQueries';
import { AuthContextType, AuthUser, UserType } from '../types/auth.types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const resolveUserType = (userData: Partial<AuthUser> | null): UserType => {
  if (!userData) {
    return null;
  }

  const candidateType = (userData.userType ?? userData.__typename) as UserType | string | undefined;

  if (candidateType === 'Borrower' || candidateType === 'Admin' || candidateType === 'Broker') {
    return candidateType;
  }

  return null;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [userType, setUserType] = useState<UserType>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  const { getUserLoggin } = useGetUserLoggin();
  const { exchangeOAuthToken } = useExchangeOAuthToken();

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const { data } = await getUserLoggin();
      
      if (data?.GetUserLoggin) {
        const userData = data.GetUserLoggin;
        const resolvedType = resolveUserType(userData as AuthUser);
        
        setUser({ ...(userData as AuthUser), userType: resolvedType });
        setUserType(resolvedType);
      } else {
        setUser(null);
        setUserType(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      setUser(null);
      setUserType(null);
    } finally {
      setIsLoading(false);
    }
  }, [getUserLoggin]);

  const login = useCallback(async (token: string) => {
    localStorage.setItem('token', token);
    await checkAuth();
  }, [checkAuth]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    setUserType(null);
  }, []);

  // Check auth on mount and handle OAuth flow
  useEffect(() => {
    const oAuthToken = new URLSearchParams(window.location.search).get('oauth');
    
    if (oAuthToken) {
      exchangeOAuthToken({
        variables: { token: oAuthToken }
      }).then(({ data }) => {
        if (data?.exchangeOAuthToken) {
          const token = data.exchangeOAuthToken;
          localStorage.setItem('token', token);
          
          // Clean up URL
          const url = new URL(window.location.href);
          url.searchParams.delete('oauth');
          window.history.replaceState({}, document.title, url.toString());
          
          checkAuth();
        }
      }).catch((error) => {
        console.error('OAuth exchange failed:', error);
        setIsLoading(false);
      });
    } else {
      checkAuth();
    }
  }, [checkAuth, exchangeOAuthToken]);

  const value: AuthContextType = {
    user,
    userType,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuth,
    isBorrower: userType === 'Borrower',
    isAdmin: userType === 'Admin',
    isBroker: userType === 'Broker'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
